import '../css/PosAdmin.css';

/**
 * An editable list of strings — one input per value, with add and remove.
 *
 * Written generically (label, placeholder and copy all come from props) so it
 * can hold any repeated single-line value, not just the staff addresses it was
 * built for.
 *
 * CONTROLLED, and the array is the source of truth: the parent holds the
 * values and gets a new array on every change. Rows are keyed by INDEX
 * deliberately — these are plain strings with no id of their own, and two rows
 * can legitimately hold the same text while someone is mid-edit, so a
 * value-based key would collapse them into one.
 *
 * An empty list renders one empty row rather than nothing, so there is always
 * somewhere to type; empty rows are dropped by the parent on save.
 *
 * @param {object} props
 * @param {string[]} props.values - current values, one per row.
 * @param {(values: string[]) => void} props.onChange - called with the whole
 *   new array whenever a row is edited, added or removed.
 * @param {string} [props.placeholder] - placeholder for each row.
 * @param {string} [props.addLabel] - text on the add button.
 * @param {string} [props.itemLabel] - singular noun for screen readers, e.g.
 *   'staff address'; used to label each row and its remove button.
 * @param {boolean} [props.disabled] - disables every control, e.g. while saving.
 * @param {string} [props.type] - input type; 'email' gets the right keyboard on
 *   a tablet, which is where this is actually used.
 * @returns {JSX.Element}
 */
function StringListInput({
  values,
  onChange,
  placeholder = '',
  addLabel = 'Add',
  itemLabel = 'item',
  disabled = false,
  type = 'text',
}) {
  // Never render zero rows: a list with no input at all looks broken, and the
  // only way out would be the add button, which reads as a second step.
  const rows = values.length > 0 ? values : [''];

  /**
   * Replaces one row.
   * @param {number} index
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const handleChange = (index) => (event) => {
    const next = [...rows];
    next[index] = event.target.value;
    onChange(next);
  };

  /**
   * Removes one row. Removing the last one leaves a single empty row rather
   * than an empty list, for the reason above.
   * @param {number} index
   * @returns {void}
   */
  const handleRemove = (index) => {
    const next = rows.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : ['']);
  };

  /** Appends an empty row for the next value. @returns {void} */
  const handleAdd = () => onChange([...rows, '']);

  return (
    <div className="pa-list">
      {rows.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="pa-list-row" key={index}>
          <input
            className="pa-input pa-list-input"
            type={type}
            value={value}
            onChange={handleChange(index)}
            placeholder={placeholder}
            disabled={disabled}
            spellCheck="false"
            aria-label={`${itemLabel} ${index + 1}`}
          />
          <button
            type="button"
            className="pa-btn pa-btn-quiet pa-list-remove"
            onClick={() => handleRemove(index)}
            disabled={disabled}
            aria-label={`Remove ${itemLabel} ${index + 1}`}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        className="pa-btn pa-btn-quiet pa-list-add"
        onClick={handleAdd}
        disabled={disabled}
      >
        {addLabel}
      </button>
    </div>
  );
}

export default StringListInput;
